import React from "react";
import { X, FilePlus2, SkipForward } from "lucide-react";

/**
 * Decision modal shown after a SOM advances to "Aguarda classificação"
 * ({kind: "pcp"}) or to "Aprovado" ({kind: "pec"}).
 *
 * Replaces the intermediate Auto-Task confirmation modal for these two
 * transitions with a direct choice:
 *   Primary  → open the PCP/PEC creation form pre-filled from the SOM.
 *   Skip     → log an audit entry (via `/skip-classification` /
 *              `/skip-parts-request`) and let the SOM continue forward
 *              without the linked record.
 *
 * The parent decides what "open the form" means (mount NewClassificationRequestDialog
 * or NewPartsRequestDialog).
 */
export default function WorkflowDecisionDialog({ kind, caseNumber, onCreate, onSkip, onClose }) {
  const isPcp = kind === "pcp";
  const title = isPcp ? "Criar Classificação de Peças?" : "Criar Encomenda de Peças?";
  const subtitle = isPcp
    ? `A obra ${caseNumber} avançou para "Aguarda classificação".`
    : `A obra ${caseNumber} avançou para "Aprovado".`;
  const description = isPcp
    ? "Se esta viatura precisa de classificação de peças, cria já a PCP e preenche as conclusões técnicas. Caso contrário, dispensa este passo e continua o fluxo."
    : "Se esta reparação envolve encomenda de peças, cria já a PEC e a lista de peças. Caso contrário (reparação só de mão-de-obra, garantia coberta noutro lado) dispensa este passo.";
  const primaryLabel = isPcp ? "Criar PCP" : "Criar Encomenda de Peças";
  const secondaryLabel = isPcp ? "Continuar sem PCP" : "Continuar sem PEC";
  const primaryTestId = isPcp ? "decision-create-pcp" : "decision-create-pec";
  const secondaryTestId = isPcp ? "decision-skip-pcp" : "decision-skip-pec";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={`workflow-decision-dialog-${kind}`}
    >
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8] flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Próximo passo</div>
            <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">{title}</h2>
            <div className="text-xs text-[#6C757D] mt-0.5">{subtitle}</div>
          </div>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#1A1A2E] mt-0.5"
            aria-label="Fechar"
            data-testid="decision-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-[#495057] leading-relaxed">{description}</p>
        </div>
        <div className="px-5 pb-5 flex flex-col gap-2">
          <button
            onClick={onCreate}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm transition-colors"
            data-testid={primaryTestId}
          >
            <FilePlus2 className="w-4 h-4" /> {primaryLabel}
          </button>
          <button
            onClick={onSkip}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border border-gray-300 text-[#495057] hover:bg-[#F4F6F8] rounded-sm transition-colors"
            data-testid={secondaryTestId}
          >
            <SkipForward className="w-4 h-4" /> {secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
